import React from 'react';
import styled from 'styled-components';
import { AlertCircle, Eye, Brain, Lock } from 'lucide-react';
import { theme } from '../styles/theme';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { FeatureLock } from './FeatureLock';
import {
  getIssuesForPlan,
  getIssuesByCategory,
  type PlanTier,
  type SkinIssue,
  PLAN_DISCLAIMERS,
} from '../config/skinIssues';

const IssuesContainer = styled(Card)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const EmbeddedWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid ${theme.colors.border.light};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmbeddedSectionTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const EmbeddedSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.55;
  max-width: 36rem;
`;

const Disclaimer = styled.div`
  background: ${theme.colors.background.secondary};
  border-left: 3px solid ${theme.colors.status.warning};
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const DisclaimerIcon = styled(AlertCircle)`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${theme.colors.status.warning};
`;

const CategoriesGrid = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$embedded ? '1rem' : '1.5rem')};
`;

const CategorySection = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$embedded ? '0.5rem' : '0.75rem')};
`;

const CategoryTitle = styled.h3<{ $embedded?: boolean }>`
  font-size: ${(p) => (p.$embedded ? theme.typography.fontSize.sm : theme.typography.fontSize.base)};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
  text-transform: capitalize;
`;

const IssuesList = styled.div<{ $embedded?: boolean }>`
  display: grid;
  gap: ${(p) => (p.$embedded ? '0.5rem' : '0.75rem')};
  grid-template-columns: repeat(auto-fill, minmax(${(p) => (p.$embedded ? '240px' : '280px')}, 1fr));

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const IssueCard = styled.div<{ $isLocked?: boolean; $embedded?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.5rem 0.75rem;
  align-items: start;
  background: ${(p) =>
    p.$isLocked ? theme.colors.background.secondary : theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${(p) => (p.$embedded ? '0.75rem 1rem' : '1rem')};
  opacity: ${(p) => (p.$isLocked ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(p) =>
      p.$isLocked ? theme.colors.border.light : theme.colors.primary[400]};
    box-shadow: ${(p) => (p.$isLocked ? 'none' : theme.shadows.sm)};
  }
`;

/* Column 1 only: icon + title. Title cannot enter badge column. */
const IssueTitleCell = styled.div`
  grid-column: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const IssueIcon = styled.span`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const IssueName = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
`;

/* Column 2 only: badge. Stays right, never overlaps title. */
const IssueBadgeCell = styled.div`
  grid-column: 2;
  grid-row: 1;
  flex-shrink: 0;
`;

const IssueDescription = styled.p`
  grid-column: 1 / -1;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  margin: 0;
  line-height: 1.5;
  word-break: break-word;
  min-width: 0;
`;

const DetectionBadge = styled(Badge)<{ $method: 'detected' | 'inferred' }>`
  font-size: 0.6875rem;
  padding: 0.2rem 0.4rem;
  flex-shrink: 0;
  box-sizing: border-box;
  white-space: nowrap;
  background: ${props => props.$method === 'detected' 
    ? `${theme.colors.mint[100]}` 
    : `${theme.colors.primary[100]}`};
  color: ${props => props.$method === 'detected' 
    ? theme.colors.mint[700] 
    : theme.colors.primary[700]};
  border: 1px solid ${props => props.$method === 'detected' 
    ? theme.colors.mint[300] 
    : theme.colors.primary[300]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
`;

const LockedOverlay = styled.div`
  position: relative;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.typography.fontSize.sm};
`;

interface SkinIssuesDisplayProps {
  detectedIssueIds: string[];
  currentPlanTier: PlanTier;
  onUpgrade?: () => void;
  /** When true, render inside Acne Insights card (no outer card, compact layout) */
  embedded?: boolean;
}

export const SkinIssuesDisplay: React.FC<SkinIssuesDisplayProps> = ({
  detectedIssueIds,
  currentPlanTier,
  onUpgrade,
  embedded = false,
}) => {
  // Get all issues available for current plan
  const availableIssues = getIssuesForPlan(currentPlanTier);
  
  // Filter to only show detected issues that are available for current plan
  const visibleIssues = availableIssues.filter(issue => 
    detectedIssueIds.includes(issue.id)
  );
  
  // Get issues that are detected but locked (require higher plan)
  const allIssues = getIssuesForPlan('elite'); // Get all issues to check locked ones
  const lockedIssues = allIssues.filter(issue => 
    detectedIssueIds.includes(issue.id) && 
    !visibleIssues.some(vi => vi.id === issue.id)
  );
  
  // Group visible issues by category
  const issuesByCategory = getIssuesByCategory(currentPlanTier);
  const visibleByCategory: Record<string, SkinIssue[]> = {};
  
  visibleIssues.forEach(issue => {
    if (!visibleByCategory[issue.category]) {
      visibleByCategory[issue.category] = [];
    }
    visibleByCategory[issue.category].push(issue);
  });
  
  const hasVisibleIssues = Object.keys(visibleByCategory).length > 0;
  const hasLockedIssues = lockedIssues.length > 0;
  const disclaimer = PLAN_DISCLAIMERS[currentPlanTier];

  if (!hasVisibleIssues && !hasLockedIssues) {
    if (embedded) return null;
    return (
      <IssuesContainer>
        <EmptyState>
          No specific skin issues detected in this scan. Your skin appears to be in good condition! ✨
        </EmptyState>
      </IssuesContainer>
    );
  }

  const Wrapper = (embedded ? EmbeddedWrapper : IssuesContainer) as React.ElementType;
  const SectionTitle = (embedded ? EmbeddedSectionTitle : Title) as React.ElementType;
  const SectionSubtitle = (embedded ? EmbeddedSubtitle : Subtitle) as React.ElementType;

  return (
    <Wrapper>
      <Header>
        <SectionTitle>
          <Eye size={embedded ? 18 : 20} />
          Detected Skin Patterns
        </SectionTitle>
        <SectionSubtitle>
          Based on your scan analysis. These are informational insights, not medical diagnoses.
        </SectionSubtitle>
      </Header>

      {disclaimer && !embedded && (
        <Disclaimer>
          <DisclaimerIcon size={16} />
          <span>{disclaimer}</span>
        </Disclaimer>
      )}

      {hasVisibleIssues && (
        <CategoriesGrid $embedded={embedded}>
          {Object.entries(visibleByCategory).map(([category, issues]) => (
            <CategorySection key={category} $embedded={embedded}>
              <CategoryTitle $embedded={embedded}>{category.replace('_', ' ')}</CategoryTitle>
              <IssuesList $embedded={embedded}>
                {issues.map((issue) => (
                  <IssueCard key={issue.id} $embedded={embedded}>
                    <IssueTitleCell>
                      {issue.icon && <IssueIcon>{issue.icon}</IssueIcon>}
                      <IssueName>{issue.displayName}</IssueName>
                    </IssueTitleCell>
                    <IssueBadgeCell>
                      <DetectionBadge $method={issue.detectionMethod}>
                        {issue.detectionMethod === 'detected' ? (
                          <>
                            <Eye size={10} />
                            Detected
                          </>
                        ) : (
                          <>
                            <Brain size={10} />
                            Inferred
                          </>
                        )}
                      </DetectionBadge>
                    </IssueBadgeCell>
                    <IssueDescription>{issue.description}</IssueDescription>
                  </IssueCard>
                ))}
              </IssuesList>
            </CategorySection>
          ))}
        </CategoriesGrid>
      )}

      {hasLockedIssues && (
        <LockedOverlay>
          <CategorySection $embedded={embedded}>
            <CategoryTitle $embedded={embedded}>
              Additional Insights Available
              <Lock size={14} style={{ marginLeft: '0.5rem', opacity: 0.6 }} />
            </CategoryTitle>
            <FeatureLock
              feature="Advanced Skin Insights"
              requiredTier={lockedIssues[0]?.planTier || 'pro'}
              message={`Upgrade to ${lockedIssues[0]?.planTier === 'elite' ? 'Elite' : 'Pro'} to see ${lockedIssues.length} additional skin insights`}
            >
              <IssuesList $embedded={embedded}>
                {lockedIssues.map((issue) => (
                  <IssueCard key={issue.id} $isLocked $embedded={embedded}>
                    <IssueTitleCell>
                      {issue.icon && <IssueIcon>{issue.icon}</IssueIcon>}
                      <IssueName>{issue.displayName}</IssueName>
                    </IssueTitleCell>
                    <IssueDescription>{issue.description}</IssueDescription>
                  </IssueCard>
                ))}
              </IssuesList>
            </FeatureLock>
          </CategorySection>
        </LockedOverlay>
      )}
    </Wrapper>
  );
};

